/**
 * entities/notification/api/useUnreadCount — Typed query hook for unread notification count.
 *
 * Polls GET /api/notifications/unread-count/ at a reasonable interval.
 * Polling is disabled when the user is not authenticated.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/shared/api';
import { useSessionStore } from '@/entities/session';
import type { UnreadCount } from '../model/types';

export const UNREAD_COUNT_QUERY_KEY = ['notifications', 'unread-count'] as const;

/** Polling interval for the unread count badge (60 seconds). */
const UNREAD_COUNT_POLL_INTERVAL_MS = 60_000;

/**
 * Returns the current unread notification count.
 * Polls every 60 seconds while the user is authenticated.
 * Polling stops automatically when the user is logged out.
 */
export function useUnreadCountQuery() {
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated);

  return useQuery<UnreadCount, Error>({
    queryKey: UNREAD_COUNT_QUERY_KEY,
    queryFn: async (): Promise<UnreadCount> => {
      const result = await (
        apiClient.GET as (
          p: string,
        ) => Promise<{ data?: unknown; error?: unknown; response: Response }>
      )('/api/notifications/unread-count/');

      if (result.error !== undefined) {
        const err = result.error;
        const detail =
          typeof err === 'object' && err !== null && 'detail' in err
            ? String((err as Record<string, unknown>)['detail'])
            : `API error: ${result.response.status}`;
        throw new Error(detail);
      }

      return (result.data ?? { unread_count: 0 }) as UnreadCount;
    },
    enabled: isAuthenticated,
    refetchInterval: isAuthenticated ? UNREAD_COUNT_POLL_INTERVAL_MS : false,
    staleTime: 30_000,
  });
}
